// libraries
import DeckGL, { type DeckGLRef } from '@deck.gl/react/typed'
import mapboxgl from 'mapbox-gl'
import {
  CSSProperties,
  FC,
  MutableRefObject,
  ReactNode,
  useCallback,
  useRef,
} from 'react'
import {
  FullscreenControl,
  _MapContext as MapContext,
  NavigationControl,
  StaticMap,
} from 'react-map-gl'
import Geocoder from 'react-map-gl-geocoder'

// constants
import { MAP_STYLE_TYPES, MAPBOX_LIMITS } from 'constants/map'

// utils
import { coordinatesGeocoder } from 'helpers/geojson'
import { reportException } from 'helpers/log'
import { useBranding } from 'hooks'

import type { Layer } from '@deck.gl/core/typed'
import type { MapLayer, Map as MapType, Viewport } from 'types/map'

// style
import './index.scss'

mapboxgl.workerClass =
  // eslint-disable-next-line import/no-webpack-loader-syntax,import/no-unresolved, @typescript-eslint/no-var-requires
  require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default

const mapControlsDefaultStyle = {
  position: 'absolute',
  bottom: '50px',
  left: '25px',
}

export type MapProps = {
  map: MapType
  mapRef?: MutableRefObject<DeckGLRef | undefined>
  mapCanvasRef?: MutableRefObject<HTMLElement | undefined>
  layersList?: Layer[]
  viewState?: Viewport
  onViewStateChange?: (v: { viewState: Viewport }) => void
  children?: ReactNode
  className?: string
  enableGeocoder?: boolean
  getCursor?: () => void
  getTooltip?: Parameters<typeof DeckGL>[0]['getTooltip']
  displayMapOnly?: boolean
  disableDrag?: boolean
  style?: CSSProperties
}

/**
 *
 * @param props.map - map settings
 * @param props.mapRef - ref for deckgl
 * @param props.mapCanvasRef - ref for container
 * @param props.layersList - deckgl layers specification
 * @param props.viewState - viewState
 * @param props.onViewStateChange - on view change
 * @param props.className - container class name
 * @param props.enableGeocoder - enable geocoder or not
 * @param props.getCursor - A custom callback to retrieve the cursor type.
 * @param props.displayMapOnly - whether to display mao only, no geocoder no control
 * @param props.disableDrag - whether to disable drag or not
 * @param props.style - the style for the map container
 */

const Map: FC<MapProps> = ({
  map,
  mapRef,
  mapCanvasRef,
  layersList,
  viewState,
  onViewStateChange,
  children,
  className,
  enableGeocoder,
  displayMapOnly,
  disableDrag,
  style: mapContainerStyle,
  ...rest
}) => {
  const { mapboxAccessToken } = useBranding()
  const geocoderContainerRef = useRef()

  const { id: mapId, style } = map || {}

  const staticMapRef = useRef()

  const handleGeocoderViewportChange = useCallback(
    (newViewport: Viewport) => {
      const geocoderDefaultOverrides = { transitionDuration: 1000 }
      if (!onViewStateChange) return undefined

      return onViewStateChange({
        viewState: {
          ...newViewport,
          ...geocoderDefaultOverrides,
        },
      })
    },
    [onViewStateChange]
  )

  const onDeckGlError = useCallback((error: Error, source: MapLayer) => {
    reportException(error, { source })
  }, [])
  return (
    <div
      className={`map ${className}`}
      ref={mapCanvasRef}
      style={mapContainerStyle}
    >
      {!displayMapOnly && enableGeocoder && (
        <div
          ref={geocoderContainerRef}
          style={{
            height: 50,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 4,
            zIndex: 1,
          }}
        />
      )}

      <DeckGL
        {...rest}
        ref={mapRef}
        onViewStateChange={onViewStateChange}
        viewState={{
          ...viewState,
          ...MAPBOX_LIMITS,
        }}
        controller={!disableDrag}
        layers={layersList}
        glOptions={{ preserveDrawingBuffer: true }}
        ContextProvider={MapContext.Provider}
        onError={onDeckGlError}
      >
        <StaticMap
          key={mapId}
          ref={staticMapRef}
          reuseMaps
          mapStyle={style ?? MAP_STYLE_TYPES.dark}
          mapboxApiAccessToken={mapboxAccessToken}
          preserveDrawingBuffer
          preventStyleDiffing
        >
          {!displayMapOnly && enableGeocoder && (
            <Geocoder
              mapRef={staticMapRef}
              containerRef={geocoderContainerRef}
              onViewportChange={handleGeocoderViewportChange}
              mapboxApiAccessToken={mapboxAccessToken}
              localGeocoder={coordinatesGeocoder}
              position='top-left'
            />
          )}
        </StaticMap>
        {!displayMapOnly && (
          <>
            <NavigationControl style={mapControlsDefaultStyle} />
            <FullscreenControl
              style={{ ...mapControlsDefaultStyle, bottom: '10px' }}
              container={document.querySelector('#mapContent')}
            />
          </>
        )}
        <img
          src='/assets/logo/sensorup-logo-icon-light.svg'
          className='mapLogo'
          alt='SensorUp Logo'
          width='1'
          height='1'
        />
      </DeckGL>
      {children}
    </div>
  )
}

Map.defaultProps = {
  layersList: [],
  viewState: {},
  onViewStateChange: undefined,
  mapRef: undefined,
  mapCanvasRef: undefined,
  enableGeocoder: false,
  children: undefined,
  displayMapOnly: false,
}

export default Map
